// Canonical financial calculation library.
// All monetary math goes through here. Uses big.js for arbitrary-precision decimals.
// pricing.ts stays for now — consumers migrate gradually.

import Big from 'big.js';
import type { Pricing, PricingRow, VatRateCode } from './types.ts';

// Configure Big.js: round half up (standard commercial rounding)
Big.RM = Big.roundHalfUp;

// ── Rounding ────────────────────────────────────────────────────────────

/** Round a number to 2 decimal places using arbitrary-precision arithmetic. */
export function round2(n: number): number {
  // big.js throws "[big.js] Invalid number" on NaN / Infinity / non-numeric.
  // Treat anything non-finite as 0 so callers (UI sums over partly-missing
  // transactions) don't crash when one row has a NaN weight/price.
  if (!Number.isFinite(n)) return 0;
  return new Big(n).round(2).toNumber() || 0; // avoid -0
}

// ── Weight ──────────────────────────────────────────────────────────────
//
// Cargo mass, not money — but the same rule applies: no float arithmetic.
// `transactions.unloaded_weight` is numeric(10,3) (tonnes) and weighing tickets
// are read in kg, so both helpers round to 3 decimals.

/**
 * Cargo net from a weighing ticket's gross and tare, both in the same unit.
 * Returns null when the pair cannot yield a positive net (missing, non-finite,
 * or tare >= gross — a ticket that reads that way was misread, and guessing a
 * cargo mass from it would be worse than leaving the transaction empty).
 */
export function netFromGrossTare(gross: number | null, tare: number | null): number | null {
  if (gross == null || tare == null) return null;
  if (!Number.isFinite(gross) || !Number.isFinite(tare)) return null;
  const net = new Big(gross).minus(tare).round(3).toNumber();
  return net > 0 ? net : null;
}

/** Kilograms → tonnes, rounded to 3 decimals (1 kg). */
export function kgToTonnes(kg: number): number {
  if (!Number.isFinite(kg)) return 0;
  return new Big(kg).div(1000).round(3).toNumber() || 0;
}

// ── VAT ─────────────────────────────────────────────────────────────────

/** Convert a VAT rate code to a decimal rate. '23' → 0.23, 'zw'/'np' → 0. */
export function vatRate(code: string): number {
  if (code === 'zw' || code === 'np') return 0;
  const parsed = parseInt(code, 10);
  return isNaN(parsed) ? 0 : parsed / 100;
}

/** Compute VAT amount from net + rate code, rounded to 2dp. */
export function vatAmount(net: number, vatRateCode: string): number {
  const rate = vatRate(vatRateCode);
  return new Big(net).times(rate).round(2).toNumber();
}

/** Compute gross from net + VAT rate code, rounded to 2dp. */
export function grossAmount(net: number, vatRateCode: string): number {
  const vat = vatAmount(net, vatRateCode);
  return new Big(net).plus(vat).round(2).toNumber();
}

/** Compute net, VAT, and gross from a net amount + rate code. All rounded to 2dp. */
export function netVatGross(net: number, vatRateCode: string): { net: number; vat: number; gross: number } {
  const roundedNet = round2(net);
  const vat = vatAmount(roundedNet, vatRateCode);
  const gross = new Big(roundedNet).plus(vat).round(2).toNumber();
  return { net: roundedNet, vat, gross };
}

// ── Comparison ──────────────────────────────────────────────────────────

/** Default tolerance for monetary comparisons (1 cent). */
export const MONEY_TOLERANCE = 0.01;

/** Check whether two monetary values are equal within tolerance (after rounding). */
export function moneyEq(a: number, b: number, tolerance: number = MONEY_TOLERANCE): boolean {
  return Math.abs(new Big(a).minus(b).round(2).toNumber()) <= tolerance;
}

/** Rounded difference between two monetary values. */
export function moneyDiff(a: number, b: number): number {
  return new Big(a).minus(b).round(2).toNumber();
}

/** Is an amount effectively zero (within tolerance)? */
export function isZero(n: number, tolerance: number = MONEY_TOLERANCE): boolean {
  return Math.abs(n) <= tolerance;
}

/** Absolute value, rounded to 2dp. */
export function absRound(n: number): number {
  return new Big(n).abs().round(2).toNumber();
}

// ── Aggregation ─────────────────────────────────────────────────────────

/** Sum a numeric field across items, rounded to 2dp. Nulls treated as 0. */
export function sumField<T>(items: T[], field: keyof T): number {
  let total = new Big(0);
  for (const item of items) {
    const val = item[field];
    if (typeof val === 'number') total = total.plus(val);
  }
  return total.round(2).toNumber();
}

/** Sum standard amount fields across items. All results rounded to 2dp. */
export function sumAmounts(
  items: { net_amount?: number | null; vat_amount?: number | null; gross_amount?: number | null; quantity?: number | null }[],
): { net: number; vat: number; gross: number; quantity: number } {
  let net = new Big(0);
  let vat = new Big(0);
  let gross = new Big(0);
  let qty = new Big(0);
  for (const item of items) {
    if (item.net_amount != null) net = net.plus(item.net_amount);
    if (item.vat_amount != null) vat = vat.plus(item.vat_amount);
    if (item.gross_amount != null) gross = gross.plus(item.gross_amount);
    if (item.quantity != null) qty = qty.plus(item.quantity);
  }
  return {
    net: net.round(2).toNumber(),
    vat: vat.round(2).toNumber(),
    gross: gross.round(2).toNumber(),
    quantity: qty.round(2).toNumber(),
  };
}

// ── Net calculation ─────────────────────────────────────────────────────

/** Calculate net amount: unitPrice × quantity, rounded to 2dp. */
export function calculateNet(unitPrice: number, quantity: number): number {
  return new Big(unitPrice).times(quantity).round(2).toNumber();
}

/** Calculate absolute net for a reconcile candidate. Returns null if price/type missing. */
export function candidateAbsNet(unitPrice: number | null, priceType: string | null, weight: number | null): number | null {
  if (unitPrice === null || priceType === null) return null;
  const qty = priceType === 'flat_rate' ? 1 : (weight ?? 0);
  return new Big(unitPrice).abs().times(qty).round(2).toNumber();
}

// ── Pricing types ───────────────────────────────────────────────────────

/** Standard quantity unit for weight-based pricing. */
export const UNIT_WEIGHT = 't';
/** Standard quantity unit for flat-rate pricing. */
export const UNIT_PIECE = 'szt';

export type PricingInput = PricingRow & {
  weight: number | null;
  /** If set, used as-is instead of computing from unit_price × weight. */
  net_amount?: number | null;
  /** If set, used as-is instead of computing from net × vat_rate. */
  vat_amount?: number | null;
  /** If set, used as-is instead of computing from net + vat. */
  gross_amount?: number | null;
};

// ── Pricing calculation ─────────────────────────────────────────────────

/**
 * Core pricing calculation — entity-agnostic.
 * Values flow through signed (negative price → negative amounts).
 * Uses big.js for all arithmetic.
 * Defaults (currency, VAT rate) are applied HERE and nowhere else.
 */
export function calculatePricing(input: PricingInput): Pricing {
  const isFlatRate = input.price_type === 'flat_rate';
  const price = round2(input.unit_price ?? 0);
  const weight = input.weight ?? 0;
  const qty = isFlatRate ? 1 : weight;
  const vatRateCode: VatRateCode = input.vat_rate_code ?? DEFAULT_VAT_RATE_CODE;

  // Use pre-existing amounts if set (e.g., invoice line items), otherwise compute.
  const net = input.net_amount != null ? input.net_amount : calculateNet(price, qty);
  const vat = input.vat_amount != null ? input.vat_amount : vatAmount(net, vatRateCode);
  const gross = input.gross_amount != null ? input.gross_amount : new Big(net).plus(vat).round(2).toNumber();

  return {
    quantity: qty,
    unit: isFlatRate ? UNIT_PIECE : UNIT_WEIGHT,
    unit_price: isFlatRate ? net : price,
    currency: input.currency || DEFAULT_CURRENCY,
    price_type: input.price_type ?? 'per_ton',
    vat_rate_code: vatRateCode,
    net_amount: net,
    vat_amount: vat,
    gross_amount: gross,
  };
}

// ── Display formatters ──────────────────────────────────────────────────

/** Price suffix for display: per_ton → '/t', otherwise empty. */
export function formatPriceSuffix(priceType: string | null | undefined): string {
  return priceType === 'per_ton' ? '/t' : '';
}

/** Format a VatRateCode for display: 'zw' → 'ZW', 'np' → 'NP', '23' → '23%'. */
export function formatVatRateCode(code: string | null | undefined): string {
  if (!code) return '-';
  if (code === 'zw') return 'ZW';
  if (code === 'np') return 'NP';
  return `${code}%`;
}

// ── Date ────────────────────────────────────────────────────────────────

/** Check if two YYYY-MM-DD date strings share the same year-month. */
export function isSameMonth(a: string, b: string): boolean {
  return a.slice(0, 7) === b.slice(0, 7);
}

/** True if two YYYY-MM-DD dates are in the same OR an adjacent calendar month
 *  (|month difference| ≤ 1, December→January rollover included). Used for the
 *  relaxed date gate on already-paid invoices. */
export function isSameOrAdjacentMonth(a: string, b: string): boolean {
  const am = parseInt(a.slice(0, 4), 10) * 12 + parseInt(a.slice(5, 7), 10);
  const bm = parseInt(b.slice(0, 4), 10) * 12 + parseInt(b.slice(5, 7), 10);
  return Math.abs(am - bm) <= 1;
}

/** Year-month ("YYYY-MM") of a YYYY-MM-DD date string. These are date-only values
 *  (no time/zone), so the field is read directly rather than via `new Date()`,
 *  which would reintroduce a timezone-shift bug. */
export function monthOf(date: string): string {
  return date.slice(0, 7);
}

/** First day ("YYYY-MM-01") of the calendar month AFTER `month` ("YYYY-MM").
 *  Real UTC date arithmetic so the December→January year rollover is handled by
 *  the Date engine, not by hand-rolled string math. Use as an EXCLUSIVE upper
 *  bound for "all invoices in `month` and earlier within a range". */
export function firstOfNextMonth(month: string): string {
  const [year, oneBasedMonth] = month.split('-').map(Number);
  // Date.UTC's month arg is 0-based, so passing the 1-based month lands on the
  // NEXT month (and wraps the year when oneBasedMonth === 12).
  return new Date(Date.UTC(year, oneBasedMonth, 1)).toISOString().slice(0, 10);
}

// ── Correction deltas ───────────────────────────────────────────────────

import type { DisplayPricing } from './types.ts';

/** Apply correction deltas to a base pricing. Returns DisplayPricing with original/delta if changed. */
export function applyDeltas(original: Pricing, deltas: Pricing[]): DisplayPricing {
  if (deltas.length === 0) return { ...original };

  const dQty = sumField(deltas, 'quantity');
  const dNet = sumField(deltas, 'net_amount');
  const dVat = sumField(deltas, 'vat_amount');
  const dGross = sumField(deltas, 'gross_amount');
  const dUnitPrice = sumField(deltas, 'unit_price');
  const effVatRateCode = deltas[deltas.length - 1].vat_rate_code ?? original.vat_rate_code;

  const hasDelta = !isZero(dNet) || !isZero(dQty, 0.001) || !isZero(dVat)
    || !isZero(dGross) || !isZero(dUnitPrice) || effVatRateCode !== original.vat_rate_code;

  const effQty = round2(original.quantity + dQty);
  const effNet = round2(original.net_amount + dNet);
  const result: DisplayPricing = {
    ...original,
    quantity: effQty,
    // Effective unit price is the canonical net ÷ quantity, NOT the summed unit_price.
    // A value-only correction (qty 0, price 0, only a net delta — e.g. a split-payment
    // adjustment) would otherwise leave the unit price stale (70 while net/qty = 280),
    // which breaks the exact-price match in P2. Fall back to the summed price only when
    // quantity is 0 (zeroed line — price is moot).
    unit_price: effQty !== 0 ? round2(effNet / effQty) : round2(original.unit_price + dUnitPrice),
    vat_rate_code: effVatRateCode,
    net_amount: effNet,
    vat_amount: round2(original.vat_amount + dVat),
    gross_amount: round2(original.gross_amount + dGross),
  };

  if (hasDelta) {
    result.original = { ...original };
    result.delta = {
      ...original,
      quantity: dQty, unit_price: dUnitPrice, vat_rate_code: effVatRateCode,
      net_amount: dNet, vat_amount: dVat, gross_amount: dGross,
    };
  }

  return result;
}/** Default currency for pricing when none is specified. */

export const DEFAULT_CURRENCY = 'PLN';
/** Default VAT rate code when none is specified. */

export const DEFAULT_VAT_RATE_CODE: VatRateCode = '23';

/** Default payment terms in days. */
export const DEFAULT_PAYMENT_TERMS_DAYS = 14;

// ── Transaction pricing helpers ─────────────────────────────────────────
//
// Placeholder EUR→PLN rate. Single source of truth across the codebase.
// Once a live FX feed lands, replace `EUR_PLN_RATE` with a reader fn.

export const EUR_PLN_RATE = 4.32;

/** Convert an amount in `currency` to PLN with 2-decimal rounding. */
export function toPLN(amount: number, currency: string | null | undefined): number {
  if (currency === 'EUR') return round2(amount * EUR_PLN_RATE);
  return amount;
}

interface PricingLeg {
  price?:          number | null;
  price_type?:     string | null;
  price_currency?: string | null;
}

interface PricingTx {
  load_price?:           number | null;
  unload_price?:         number | null;
  load_price_type?:      string | null;
  unload_price_type?:    string | null;
  load_price_currency?:  string | null;
  unload_price_currency?:string | null;
  loaded_weight?:        number | null;
  unloaded_weight?:      number | null;
  transports?:           PricingLeg[] | null;
}

/** Effective transaction weight: prefers unloaded, falls back to loaded. */
export function effectiveWeight(tx: PricingTx): number {
  return tx.unloaded_weight ?? tx.loaded_weight ?? 0;
}

/** Gross amount on one side: `per_ton` × weight, else flat. */
export function sideGross(tx: PricingTx, side: 'load' | 'unload'): number | null {
  const price = side === 'load' ? tx.load_price : tx.unload_price;
  if (price == null) return null;
  const type  = side === 'load' ? tx.load_price_type : tx.unload_price_type;
  const w     = effectiveWeight(tx);
  return type === 'per_ton' ? price * w : price;
}

/** Gross amount for a single transport leg: `per_ton` × weight, else flat. */
export function legGross(leg: PricingLeg, weight: number): number {
  if (leg.price == null) return 0;
  return leg.price_type === 'per_ton' ? leg.price * weight : leg.price;
}

/** PLN-converted transport cost (negative legs subtracted from margin). */
export function computeTransportCostPLN(tx: PricingTx): number {
  const w = effectiveWeight(tx);
  const legs = tx.transports ?? [];
  let total = 0;
  for (const leg of legs) {
    if (leg.price == null) continue;
    const amount = leg.price_type === 'per_ton' ? leg.price * w : leg.price;
    total += toPLN(amount, leg.price_currency);
  }
  return round2(total);
}

/** PLN margin = signed sum of load + unload + transport contributions.
 *  Sides DO NOT decide income vs cost — the price SIGN does:
 *    - positive price → income (revenue)
 *    - negative price → cost
 *  Both load and unload can be either, depending on how the deal is booked
 *  (positive load = we sell at pickup; negative unload = we pay for disposal).
 *  Transport legs follow the same rule and are conventionally negative.
 *  Null when either side's price is missing. */
export function computeMarginPLN(tx: PricingTx): number | null {
  const w = effectiveWeight(tx);
  if (tx.load_price == null || tx.unload_price == null || w === 0) return null;
  const load      = toPLN(sideGross(tx, 'load')   as number, tx.load_price_currency);
  const unload    = toPLN(sideGross(tx, 'unload') as number, tx.unload_price_currency);
  const transport = computeTransportCostPLN(tx);
  return round2(load + unload + transport);
}

/** Per-currency totals for a list of legs/prices. Pure aggregate (no formatting). */
export function sumByCurrency(
  entries: Array<{ price?: number | null; price_currency?: string | null }>,
): Record<string, number> {
  const totals: Record<string, number> = {};
  for (const e of entries) {
    if (e.price == null) continue;
    const ccy = e.price_currency ?? 'EUR';
    totals[ccy] = (totals[ccy] ?? 0) + Math.abs(e.price);
  }
  return totals;
}

